<template>
  <Teleport to="body">
    <Transition name="recharge-result-fade">
      <div v-if="result" class="recharge-result-overlay" @click.self="$emit('close')">
        <section
          class="recharge-result-modal"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
        >
          <button
            type="button"
            class="recharge-result-close"
            :aria-label="$t('recharge.result.close')"
            @click="$emit('close')"
          >
            &times;
          </button>

          <div class="recharge-result-icon" :class="`is-${displayStatus}`" aria-hidden="true">
            <span v-if="displayStatus === 'success'">&#10003;</span>
            <span v-else>!</span>
          </div>

          <h2 :id="titleId" class="recharge-result-title">{{ title }}</h2>
          <p class="recharge-result-description">{{ description }}</p>

          <div v-if="result.userId || result.avatar" class="recharge-result-user">
            <img
              v-if="result.avatar"
              :src="result.avatar"
              class="recharge-result-avatar"
              alt=""
            >
            <div v-else class="recharge-result-avatar recharge-result-avatar-placeholder"></div>
            <div class="recharge-result-user-info">
              <strong v-if="result.nickname">{{ result.nickname }}</strong>
              <span>{{ $t('recharge.userId') }}: {{ result.userId || '-' }}</span>
            </div>
          </div>

          <div class="recharge-result-amount">
            <span>{{ $t('recharge.result.topUpAmount') }}</span>
            <strong>
              <img :src="currencyIcon" alt="">
              {{ formattedAmount }}
              <span class="recharge-result-currency">{{ currencyLabel }}</span>
            </strong>
          </div>

          <button type="button" class="recharge-result-done" @click="$emit('close')">
            {{ $t('recharge.result.done') }}
          </button>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import coinIcon from '@/img/recharge/oumi_coin.webp'
import gemIcon from '@/img/gem/gem_icon.webp'

const props = defineProps({
  result: {
    type: Object,
    default: null,
  },
})

defineEmits(['close'])

const { t } = useI18n()
const titleId = 'recharge-result-title'

const displayStatus = computed(() => props.result?.status === 'success' ? 'success' : 'failed')
const title = computed(() => t(`recharge.result.${displayStatus.value}Title`))
const currencyIcon = computed(() => (
  props.result?.rechargeType === 'gems' ? gemIcon : coinIcon
))
const currencyLabel = computed(() => t(`recharge.result.${props.result?.rechargeType === 'gems' ? 'gems' : 'coins'}`))
const formattedAmount = computed(() => {
  if (props.result?.amount === '' || props.result?.amount == null) return '—'
  const amount = Number(props.result?.amount)
  return Number.isFinite(amount)
    ? amount.toLocaleString('en-US')
    : `${props.result?.amount ?? ''}`
})
const description = computed(() => {
  if (props.result?.message) return props.result.message
  const currency = props.result?.rechargeType === 'gems' ? 'gems' : 'coins'
  return t(`recharge.result.${displayStatus.value}Description`, {
    currency: t(`recharge.result.${currency}`),
  })
})
</script>

<style lang="scss" scoped>
.recharge-result-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(4px);
}

.recharge-result-modal {
  position: relative;
  width: 390px;
  max-width: 100%;
  padding: 40px 28px 28px;
  border-radius: 24px;
  box-sizing: border-box;
  background: #ffffff;
  color: #111111;
  text-align: center;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.4);
  font-family: 'Poppins', sans-serif;
}

.recharge-result-close {
  position: absolute;
  top: 12px;
  right: 14px;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #777777;
  font-size: 30px;
  line-height: 34px;
  cursor: pointer;
}

.recharge-result-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 34px;
  line-height: 1;
  font-weight: 700;

  &.is-success { background: #20b26b; }
  &.is-failed { background: #ef5261; }
}

.recharge-result-title {
  margin: 0;
  font-size: 24px;
  line-height: 32px;
  font-weight: 700;
}

.recharge-result-description {
  margin: 8px 0 22px;
  color: #666666;
  font-size: 15px;
  line-height: 22px;
}

.recharge-result-user {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border-radius: 12px;
  background: #f5f6f8;
  text-align: left;
  font-size: 14px;
}

.recharge-result-avatar {
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  border-radius: 50%;
  object-fit: cover;
}

.recharge-result-avatar-placeholder {
  background: #d9dce2;
}

.recharge-result-user-info {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;

  strong,
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    font-size: 16px;
    line-height: 22px;
  }

  span {
    color: #777777;
    font-size: 13px;
    line-height: 18px;
  }
}

.recharge-result-currency {
  color: #777777;
  font-size: 12px;
  font-weight: 500;
}

.recharge-result-amount {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 12px;
  padding: 15px 16px;
  border-radius: 12px;
  background: #f5f6f8;
  font-size: 14px;

  > span { color: #777777; }

  strong {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 17px;
  }

  img {
    width: 20px;
    height: 20px;
    object-fit: contain;
  }
}

.recharge-result-done {
  width: 100%;
  height: 52px;
  margin-top: 20px;
  border: 0;
  border-radius: 26px;
  background: linear-gradient(90deg, #4843fe 0%, #ff4ba7 100%);
  color: #ffffff;
  font: inherit;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
}

.recharge-result-fade-enter-active,
.recharge-result-fade-leave-active {
  transition: opacity 0.2s ease;

  .recharge-result-modal {
    transition: transform 0.2s ease, opacity 0.2s ease;
  }
}

.recharge-result-fade-enter-from,
.recharge-result-fade-leave-to {
  opacity: 0;

  .recharge-result-modal {
    opacity: 0;
    transform: translateY(12px) scale(0.98);
  }
}

@media (max-width: 480px) {
  .recharge-result-overlay { padding: 20px; }
  .recharge-result-modal {
    padding: 36px 20px 22px;
    border-radius: 20px;
  }
  .recharge-result-title { font-size: 22px; }
}
</style>
